import { useEffect, useMemo, useRef, useState } from "react";
import { trackEvent } from "../analytics";
import { championsData, type Champion } from "../data/champions";

export function ChampionsPage() {
  // Latest year first, matching the Ligas page year selector.
  const years = useMemo(
    () => [...championsData].sort((a, b) => b.year - a.year),
    [],
  );

  const [year, setYear] = useState<number>(
    years[0]?.year ?? new Date().getFullYear(),
  );

  const current = years.find((y) => y.year === year) ?? years[0];
  const champions = current?.champions ?? [];

  // Lightbox: openIndex is an index into the current year's `champions` array.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Close the lightbox when switching years so the user isn't left staring at
  // a card from another season.
  useEffect(() => {
    setOpenIndex(null);
  }, [year]);

  // Precompute indices of champions that have an image — the only ones that
  // can be opened, and the set we navigate prev/next through.
  const imagedIndices = useMemo(
    () =>
      champions
        .map((c, i) => (c.image ? i : -1))
        .filter((i) => i >= 0),
    [champions],
  );

  function open(idx: number) {
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    setOpenIndex(idx);
    const champion = champions[idx];
    trackEvent("champion_lightbox_open", {
      year,
      index: idx,
      has_image: !!champion?.image,
    });
  }

  function close() {
    setOpenIndex(null);
    // Restore focus to the card that opened the lightbox.
    requestAnimationFrame(() => lastFocusedRef.current?.focus());
  }

  const openChampion = openIndex != null ? champions[openIndex] : null;
  const pos = openIndex != null ? imagedIndices.indexOf(openIndex) : -1;
  const hasPrev = pos > 0;
  const hasNext = pos >= 0 && pos < imagedIndices.length - 1;

  function goPrev() {
    if (hasPrev) setOpenIndex(imagedIndices[pos - 1]!);
  }
  function goNext() {
    if (hasNext) setOpenIndex(imagedIndices[pos + 1]!);
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Galeria
          </div>
          <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
            Campeões / Campeãs <span className="text-brand-700">{year}</span>
          </h1>
        </div>

        {years.length > 0 && (
          <div className="flex gap-1.5 bg-white border border-line rounded-xl p-1.5">
            {years.map((y) => {
              const active = y.year === year;
              return (
                <button
                  key={y.year}
                  type="button"
                  onClick={() => {
                    setYear(y.year);
                    trackEvent("champions_year_change", { year: y.year });
                  }}
                  className={[
                    "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                    active
                      ? "bg-brand-700 text-white"
                      : "text-[#6b6356] hover:bg-[#f3efe6]",
                  ].join(" ")}
                >
                  {y.year}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <p className="max-w-[620px] text-[15px] leading-relaxed text-[#6b6356]">
        A cada liga encerrada, o campeão leva para casa uma carta personalizada.
        Veja a galeria da temporada, da liga mais recente à mais antiga.
      </p>

      <div className="text-[13px] text-[#8a8170] font-semibold">
        {champions.length} {champions.length === 1 ? "liga" : "ligas"} premiadas
      </div>

      {champions.length === 0 ? (
        <p className="text-slate-500">Sem campeões registrados para este ano.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-7 items-start">
          {champions.map((c, i) => (
            <div
              key={c.image || c.league}
              className="flex flex-col gap-3 min-w-0"
            >
              <h3 className="font-display text-base font-bold tracking-tight leading-snug text-ink text-balance">
                {c.league}
              </h3>

              {c.image ? (
                <button
                  type="button"
                  onClick={() => open(i)}
                  aria-label={`Ampliar carta — ${c.league}`}
                  className="group block w-full bg-white border border-line rounded-2xl p-3 shadow-sm cursor-zoom-in transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700/60 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                >
                  <img
                    src={c.image}
                    alt={`Carta do campeão — ${c.league}`}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto rounded-lg transition-transform group-hover:scale-[1.01]"
                  />
                </button>
              ) : (
                <div className="bg-white border border-line rounded-2xl p-3 shadow-sm">
                  <div className="aspect-[791/1107] rounded-lg border border-dashed border-[#d9d2c4] bg-[repeating-linear-gradient(45deg,#efece6_0_11px,#f3efe6_11px_22px)] flex flex-col items-center justify-center gap-2 text-center p-4">
                    <span className="w-[34px] h-[34px] rounded-[9px] bg-white border border-line flex items-center justify-center">
                      <span className="block w-[13px] h-[18px] rounded-sm border-2 border-[#b9af9d]" />
                    </span>
                    <span className="font-mono text-[11.5px] text-[#9a9182]">
                      imagem do campeão
                    </span>
                  </div>
                </div>
              )}

              {c.champion && (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-none text-[10px] font-bold uppercase tracking-wide text-brand-700 bg-brand-50 rounded px-1.5 py-0.5">
                    Campeão
                  </span>
                  <span className="text-[15px] font-semibold text-ink truncate">
                    {c.champion}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {openChampion?.image && (
        <ChampionLightbox
          champion={openChampion}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPrev={goPrev}
          onNext={goNext}
          onClose={close}
        />
      )}
    </div>
  );
}

interface ChampionLightboxProps {
  champion: Champion;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

function ChampionLightbox({
  champion,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: ChampionLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus the close button on open + restore body scroll + keyboard shortcuts.
  useEffect(() => {
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft" && hasPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && hasNext) {
        e.preventDefault();
        onNext();
      }
    }
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Carta do campeão — ${champion.league}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 animate-[fadeIn_120ms_ease-out]"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Fechar"
        className="absolute top-3 right-3 sm:top-5 sm:right-5 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Carta anterior"
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
      )}

      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Próxima carta"
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}

      <figure
        onClick={(e) => e.stopPropagation()}
        className="relative flex flex-col items-center gap-3 max-w-[min(95vw,820px)]"
      >
        <img
          src={champion.image}
          alt={`Carta do campeão — ${champion.league}`}
          className="block w-auto max-w-full max-h-[80vh] h-auto rounded-xl shadow-2xl"
        />
        <figcaption className="text-center text-white/90 px-2">
          <div className="font-display font-bold text-base sm:text-lg leading-tight text-balance">
            {champion.league}
          </div>
          {champion.champion && (
            <div className="text-sm text-white/70 mt-0.5">
              Campeão: {champion.champion}
            </div>
          )}
        </figcaption>
      </figure>
    </div>
  );
}
